import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Chip,
  Autocomplete,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "@/store";
import { createEvent } from "@/store/eventsSlice";
import { fetchRestaurants } from "@/store/restaurantsSlice";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as usersApi from "@api/users.api";
import type { InviteableUser } from "@api/users.api";

const schema = yup.object({
  name: yup.string().required(),
  restaurantId: yup.string().optional().default(""),
  startedAt: yup.string().required(),
  endedAt: yup.string().optional().default(""),
});

type FormData = yup.InferType<typeof schema>;

export default function CreateEvent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: restaurants } = useAppSelector((s) => s.restaurants);
  const { notify } = useNotification();

  const [users, setUsers] = useState<InviteableUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<InviteableUser[]>([]);
  const [userSearch, setUserSearch] = useState("");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    dispatch(fetchRestaurants());
  }, [dispatch]);

  useEffect(() => {
    const trimmed = userSearch.trim();
    if (trimmed.length < 3) {
      setUsers([]);
      return;
    }

    const timer = window.setTimeout(() => {
      usersApi
        .search(trimmed)
        .then(setUsers)
        .catch(() => setUsers([]));
    }, 300);

    return () => window.clearTimeout(timer);
  }, [userSearch]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        name: data.name,
        startedAt: new Date(data.startedAt).toISOString(),
        restaurantId: data.restaurantId || undefined,
        endedAt: data.endedAt
          ? new Date(data.endedAt).toISOString()
          : undefined,
        userIds: selectedUsers.map((u) => u.id),
      };
      await dispatch(createEvent(payload)).unwrap();
      notify(t("events.create"), "success");
      navigate("/dashboard");
    } catch (err) {
      notify(extractErrorMessage(err, t("common.unexpectedError")), "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
          }}
        >
          {t("events.create")}
        </Typography>
      </Box>
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register("name")}
              label={t("events.name")}
              placeholder={t("events.namePlaceholder")}
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={{ mb: 2 }}
            />

            <Controller
              name="restaurantId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label={t("events.restaurant")}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">{t("common.none")}</MenuItem>
                  {restaurants.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <TextField
              {...register("startedAt")}
              label={t("events.startDate")}
              type="datetime-local"
              fullWidth
              error={!!errors.startedAt}
              sx={{ mb: 2 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              {...register("endedAt")}
              label={t("events.endDate")}
              type="datetime-local"
              fullWidth
              sx={{ mb: 2 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("events.inviteFriends")}
            </Typography>
            <Autocomplete
              multiple
              options={users}
              filterOptions={(x) => x}
              inputValue={userSearch}
              onInputChange={(_, value) => setUserSearch(value)}
              getOptionLabel={(u) =>
                u.displayName?.trim()
                  ? `${u.displayName} (${u.email})`
                  : u.email
              }
              value={selectedUsers}
              onChange={(_, val) => setSelectedUsers(val)}
              renderValue={(value, getItemProps) =>
                value.map((u, index) => (
                  <Chip
                    label={u.displayName?.trim() || u.email}
                    size="small"
                    {...getItemProps({ index })}
                    key={u.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("events.searchUsers")}
                  size="small"
                />
              )}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={() => navigate(-1)}>{t("common.cancel")}</Button>
              <Button type="submit" variant="contained">
                {t("common.create")}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
